import React from 'react';
import { Check, Lock } from 'lucide-react';
import { useEditorStore } from '../../store/editorStore';
import { overlaysApi } from '../../api/overlays';
import { track, EVENTS } from '../../lib/mixpanel';
import {
  OverlayAnchor,
  OverlaySize,
  OVERLAY_SIZE_LABELS,
} from '../../types';
import styles from './OverlayPanel.module.css';

const ANCHORS: { key: OverlayAnchor; label: string }[] = [
  { key: 'top-left', label: 'TL' },
  { key: 'top-center', label: 'TC' },
  { key: 'top-right', label: 'TR' },
  { key: 'middle-left', label: 'ML' },
  { key: 'middle-center', label: 'MC' },
  { key: 'middle-right', label: 'MR' },
  { key: 'bottom-left', label: 'BL' },
  { key: 'bottom-center', label: 'BC' },
  { key: 'bottom-right', label: 'BR' },
];

const ALL_ANCHOR_KEYS: OverlayAnchor[] = ANCHORS.map((a) => a.key);
const SIZES: OverlaySize[] = ['xs', 'sm', 'md', 'lg', 'xl'];

interface OverlayPanelProps {
  canvasWidth: number;
  canvasHeight: number;
}

export const OverlayPanel: React.FC<OverlayPanelProps> = () => {
  const { overlays, overlayConfig, brandingOverlayConfig, setOverlayConfig, updateOverlayConfig } = useEditorStore();

  const brandingAnchor = brandingOverlayConfig?.anchor ?? null;

  const pickFreeAnchor = (): OverlayAnchor => {
    return ALL_ANCHOR_KEYS.find((a) => a !== brandingAnchor) ?? 'bottom-right';
  };

  const handleSelectOverlay = (overlayId: string) => {
    if (overlayConfig?.overlayId === overlayId) {
      track(EVENTS.OVERLAY_REMOVED);
      setOverlayConfig(null);
      return;
    }
    track(EVENTS.OVERLAY_SELECTED, { overlay_id: overlayId });
    setOverlayConfig({
      overlayId,
      anchor: pickFreeAnchor(),
      size: 'md',
      opacity: 1,
    });
  };

  const brandingOverlay = brandingOverlayConfig
    ? overlays.find((o) => o._id === brandingOverlayConfig.overlayId)
    : null;

  const userOverlays = overlays.filter(
    (o) => o._id !== brandingOverlayConfig?.overlayId,
  );

  return (
    <div className={styles['container']}>
      {/* Branding overlay — always applied, read-only */}
      {brandingOverlay && brandingOverlayConfig && (
        <div className={styles['branding-section']}>
          <p className={styles['control-label']}>Branding overlay</p>
          <div className={styles['branding-row']}>
            <img
              src={overlaysApi.fileUrl(brandingOverlay._id)}
              alt={brandingOverlay.name}
              className={styles['branding-thumb']}
            />
            <div className={styles['branding-info']}>
              <span className={styles['branding-name']}>{brandingOverlay.name}</span>
              <span className={styles['branding-badge']}>
                <Lock className={styles['icon-lock']} />
                Always applied
              </span>
            </div>
          </div>
        </div>
      )}

      {/* User overlay selection */}
      {userOverlays.length > 0 && (
        <div>
          <h3 className={styles['title']}>Add your overlay</h3>
          {overlayConfig && (
            <button
              onClick={() => { track(EVENTS.OVERLAY_REMOVED); setOverlayConfig(null); }}
              className={styles['remove-link']}
            >
              Remove overlay
            </button>
          )}
          <div className={styles['overlay-grid']}>
            {userOverlays.map((overlay) => {
              const isActive = overlayConfig?.overlayId === overlay._id;
              return (
                <button
                  key={overlay._id}
                  onClick={() => handleSelectOverlay(overlay._id)}
                  className={[
                    styles['overlay-btn'],
                    isActive ? styles['overlay-btn-active'] : '',
                  ].filter(Boolean).join(' ')}
                >
                  <img
                    src={overlaysApi.fileUrl(overlay._id)}
                    alt={overlay.name}
                    className={styles['overlay-img']}
                  />
                  {isActive && (
                    <div className={styles['check-badge']}>
                      <Check className={`${styles['icon-xs']} ${styles['overlay-name-text']}`} strokeWidth={3} />
                    </div>
                  )}
                  <div className={styles['overlay-name-bar']}>
                    <p className={styles['overlay-name-text']}>{overlay.name}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* No overlays at all */}
      {!brandingOverlay && userOverlays.length === 0 && (
        <div className={styles['empty-state']}>
          <p>No overlays available.</p>
          <p>Upload PNGs in the event settings.</p>
        </div>
      )}

      {/* Config controls — only when a user overlay is selected */}
      {overlayConfig && (
        <>
          {/* Anchor position */}
          <div>
            <p className={styles['control-label']}>Position</p>
            <div className={styles['anchor-grid']}>
              {ANCHORS.map((a) => {
                const isReserved = a.key === brandingAnchor;
                const isActive = overlayConfig.anchor === a.key;
                return (
                  <button
                    key={a.key}
                    onClick={() => !isReserved && updateOverlayConfig({ anchor: a.key })}
                    disabled={isReserved}
                    title={isReserved ? 'Reserved for branding overlay' : undefined}
                    className={[
                      styles['anchor-btn'],
                      isActive ? styles['anchor-btn-active'] : '',
                      isReserved ? styles['anchor-btn-reserved'] : '',
                    ].filter(Boolean).join(' ')}
                  >
                    {isReserved ? <Lock className={styles['icon-lock']} /> : a.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Size */}
          <div>
            <p className={styles['control-label']}>Size</p>
            <div className={styles['size-row']}>
              {SIZES.map((s) => (
                <button
                  key={s}
                  onClick={() => updateOverlayConfig({ size: s })}
                  className={[
                    styles['size-btn'],
                    overlayConfig.size === s ? styles['size-btn-active'] : '',
                  ].filter(Boolean).join(' ')}
                  title={OVERLAY_SIZE_LABELS[s]}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Opacity */}
          <div>
            <div className={styles['opacity-header']}>
              <span>Opacity</span>
              <span className={styles['opacity-value']}>{Math.round(overlayConfig.opacity * 100)}%</span>
            </div>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={overlayConfig.opacity}
              onChange={(e) => updateOverlayConfig({ opacity: parseFloat(e.target.value) })}
              className={styles['range-input']}
            />
          </div>
        </>
      )}
    </div>
  );
};
